import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import saveAs from 'file-saver';
import dayjs from 'dayjs';
import { write, utils } from 'xlsx';
import {
  ExpenseStatus,
  deleteExpenseAsync,
  expenseStatusKorean,
  getExpenseListAsync,
} from '../../modules/expenses';
import { getExpenseType, s2ab } from '../../util/utils';
import { RootState } from '../../modules';

import {
  getExpenseCommon,
  syncCardApprovalList,
  syncTaxInvoiceList,
} from '../../api/constlink';
import ExpenseListComponent from '../../components/expenses/ExpenseListComponent';
import { Alert, Col, Modal, Row, Spin } from 'antd';
import SearchExpenseComponent from '../../components/expenses/SearchExpenseComponent';
import ExpenseDetailDrawer from './ExpenseDetailDrawer';

interface ReceiptListContainerProps {
  enterpriseId: string;
}

function ReceiptListContainer({ enterpriseId }: ReceiptListContainerProps) {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector(
    (state: RootState) => state.expenses.expenses,
  );
  const [expenseCommon, setExpenseCommon] = useState<any>();
  const [searchData, setSearchData] = useState<any>({
    member: -1,
    project: -1,
    category: '',
    companyName: '',
    isRefund: '',
    startDate: dayjs().startOf('month'),
    endDate: dayjs().endOf('month'),
    expenseStatus: ExpenseStatus.SUBMITTED,
  });
  const [visibleDetailDrawer, setVisibleDetailDrawer] =
    useState<boolean>(false);
  const [detailDrawerConfig, setDetailDrawerConfig] = useState<any>();
  const [syncCardApprovalLoading, setSyncCardApprovalLoading] = useState(false);
  const [syncTaxInvoiceLoading, setSyncTaxInvoiceLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [modalText, setModalText] = useState(
    '카드사 홈페이지에서 카드승인내역을 불러옵니다.',
  );

  useEffect(() => {
    dispatch(
      getExpenseListAsync.request({
        enterpriseId,
        filterData: {
          startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
          endDate: dayjs().endOf('month').add(1, 'day').format('YYYY-MM-DD'),
          expenseStatus: ExpenseStatus.SUBMITTED,
          category: undefined,
          companyName: undefined,
          isRefund: undefined,
          memberId: undefined,
        },
      }),
    );
    (async () => {
      const _expenseCommon = await getExpenseCommon(enterpriseId);
      setExpenseCommon({
        ..._expenseCommon,
        expenseStatus: ExpenseStatus.SUBMITTED,
      });
    })();
  }, [dispatch, enterpriseId]);

  const onDeleteExpense = (expenseId: string) => {
    dispatch(
      deleteExpenseAsync.request({
        enterpriseId,
        expenseId,
      }),
    );
  };

  const moveToDetailPage = (expenseId: string) => {
    setDetailDrawerConfig({ enterpriseId, expenseId });
    setVisibleDetailDrawer(true);
  };

  const downloadFile = async (data: any[]) => {
    const _data: any[] = [];
    for (let i = 0; i < data.length; i++) {
      const val: any[] = [];
      val.push(dayjs(data[i].usedDateTime).format('YYYY-MM-DD HH:mm:ss'));
      val.push(data[i].category);
      val.push(data[i].gongjong ?? '기타');
      val.push(getExpenseType(data[i]));
      val.push(expenseStatusKorean[data[i].expenseStatus as ExpenseStatus]);
      val.push(data[i].companyName);
      val.push(data[i].description);
      val.push(data[i].isRefund ? '예' : '아니오');
      val.push(data[i].price - data[i].vat);
      val.push(data[i].vat);
      val.push(data[i].price);
      val.push(data[i].usedBy ? data[i].usedBy.name : '');
      val.push(data[i].project ? data[i].project.name : '');
      _data.push(val);
    }
    var wb = utils.book_new();
    const ws = await utils.json_to_sheet(_data);
    [
      '날짜',
      '용도',
      '공정',
      '종류',
      '상태',
      '업체명',
      '설명',
      '경비환급',
      '공급가액',
      '부가세',
      '합계',
      '사용자',
      '현장명',
    ].forEach((x, idx) => {
      const cellAdd = utils.encode_cell({ c: idx, r: 0 });
      ws[cellAdd].v = x;
    });
    utils.book_append_sheet(wb, ws, `경비내역`);
    const wbout = write(wb, { bookType: 'xlsx', type: 'binary' });
    saveAs(
      new Blob([s2ab(wbout)], { type: 'application/octet-stream' }),
      `지출내역서_${dayjs().format('YYYY-MM-DD HH:mm:ss')}.xlsx`,
    );
  };

  const _searchExpenses = (data: any) => {
    const filterData: any = {
      ...data,
      startDate: dayjs(data.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(data.endDate).add(1, 'day').format('YYYY-MM-DD'),
    };

    if (data.member !== -1) filterData.memberId = data.member;
    else filterData.memberId = undefined;
    if (data.companyName === '') filterData.companyName = undefined;
    if (data.category === '') filterData.category = undefined;
    if (data.isRefund === '') filterData.isRefund = undefined;
    if (data.project !== -1) filterData.projectId = data.project;
    else filterData.projectId = undefined;
    dispatch(
      getExpenseListAsync.request({
        enterpriseId,
        filterData: {
          ...filterData,
        },
      }),
    );
    setSearchData({
      ...data,
      startDate: dayjs(data.startDate),
      endDate: dayjs(data.endDate),
    });
  };

  const onCloseDetailDrawer = () => setVisibleDetailDrawer(false);

  const _syncCardApprovalList = async () => {
    setModalText('카드승인내역을 불러오는 중입니다.');
    setSyncCardApprovalLoading(true);
    await syncCardApprovalList({ enterpriseId });
    setSearchData({
      ...searchData,
      startDate: dayjs().startOf('month'),
      endDate: dayjs().endOf('month'),
      expenseStatus: ExpenseStatus.EDITING,
    });
    dispatch(
      getExpenseListAsync.request({
        enterpriseId,
        filterData: {
          startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
          endDate: dayjs().endOf('month').add(1, 'day').format('YYYY-MM-DD'),
          expenseStatus: ExpenseStatus.EDITING,
        },
      }),
    );
    setSyncCardApprovalLoading(false);
    setOpen(false);
  };

  const _syncTaxInvoiceList = async () => {
    setSyncTaxInvoiceLoading(true);
    await syncTaxInvoiceList({ enterpriseId });
    setSearchData({
      ...searchData,
      startDate: dayjs().startOf('month'),
      endDate: dayjs().endOf('month'),
      expenseStatus: ExpenseStatus.EDITING,
    });
    dispatch(
      getExpenseListAsync.request({
        enterpriseId,
        filterData: {
          startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
          endDate: dayjs().endOf('month').add(1, 'day').format('YYYY-MM-DD'),
          expenseStatus: ExpenseStatus.EDITING,
        },
      }),
    );
    setOpen(false);
    setSyncTaxInvoiceLoading(false);
  };

  const _initSearchForm = () => {
    setSearchData({
      member: -1,
      project: -1,
      category: '',
      companyName: '',
      isRefund: '',
      startDate: dayjs().startOf('month'),
      endDate: dayjs().endOf('month'),
      expenseStatus: ExpenseStatus.SUBMITTED,
    });
  };

  return (
    <>
      {error && (
        <Alert
          message={`${error.response?.data.message}`}
          type="error"
          showIcon
          closable
        />
      )}
      <Row justify="space-between">
        <Col span={4} style={{ marginTop: '10px' }}>
          {expenseCommon && searchData && (
            <SearchExpenseComponent
              expenseCommon={expenseCommon}
              searchData={searchData}
              searchExpenses={_searchExpenses}
              initSearchForm={_initSearchForm}
            />
          )}
        </Col>
        {data && (
          <Col span={20}>
            <ExpenseListComponent
              data={data}
              onDelete={onDeleteExpense}
              moveToDetailPage={moveToDetailPage}
              downloadFile={downloadFile}
              syncCardApprovalList={() => setOpen(true)}
              syncTaxInvoiceList={_syncTaxInvoiceList}
              syncCardApprovalLoading={syncCardApprovalLoading}
              syncTaxInvoiceLoading={syncTaxInvoiceLoading}
            />
          </Col>
        )}
        {loading && (
          <Col>
            <Spin />
          </Col>
        )}
      </Row>
      {detailDrawerConfig && (
        <ExpenseDetailDrawer
          config={detailDrawerConfig}
          visible={visibleDetailDrawer}
          onClose={onCloseDetailDrawer}
        />
      )}

      <Modal
        title="카드승인내역 불러오기"
        open={open}
        onOk={_syncCardApprovalList}
        confirmLoading={syncCardApprovalLoading}
        onCancel={() => setOpen(false)}
      >
        <p>{modalText}</p>
      </Modal>
    </>
  );
}

export default ReceiptListContainer;
